import type { AgentAlarmSeverity, AgentCommandStatus, AgentStatus } from "@station/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";

/**
 * İstasyon ajanı modülünün sorguları (Prompt 14 — web).
 *
 * Okuma `agents.read`; komut gönderme / iptal `agents.command`; kayıt kodu üretme / iptali ve ajan iptali
 * `agents.manage`. Modül `STATION_AGENT` bayrağına bağlıdır; kapalıysa API `403 FEATURE_DISABLED` döner.
 *
 * **Tazeleme:** heartbeat HER SEFERİNDE olay ÜRETMEZ (yalnızca çevrimiçi/çevrimdışı geçişleri ve alarmlar
 * olay yazar) → liste, özet ve ayrıntı 30 saniyede bir yeniden çekilir. Gerçek zamanlı olaylar bunun ÜSTÜNE
 * gelir (`lib/realtime.ts > INVALIDATIONS`).
 */
const LIVE_REFRESH_MS = 30_000;

export function useAgentPermissions(): {
  enabled: boolean;
  canRead: boolean;
  canCommand: boolean;
  canManage: boolean;
} {
  const permissions = usePermissions();
  return {
    enabled: useFeatures().has("STATION_AGENT"),
    canRead: permissions.has("agents.read"),
    canCommand: permissions.has("agents.command"),
    canManage: permissions.has("agents.manage"),
  };
}

// ── İstasyon panosu özeti ────────────────────────────────────────────────────
export function useAgentsSummary(companyId: string, enabled = true) {
  const query = { companyId };
  return useQuery({
    queryKey: queryKeys.agents(companyId, "summary", query),
    enabled,
    refetchInterval: LIVE_REFRESH_MS,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/agents/summary", { params: { query } })).data!,
  });
}

// ── Ajan listesi ─────────────────────────────────────────────────────────────
export interface AgentFilters {
  stationId?: string | undefined;
  status?: AgentStatus | undefined;
  online?: boolean | undefined;
}

export function useAgents(companyId: string, filters: AgentFilters, cursor?: string, enabled = true) {
  const query: Record<string, string | number> = {
    companyId,
    limit: 25,
    ...(filters.stationId ? { stationId: filters.stationId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.online === undefined ? {} : { online: filters.online ? "true" : "false" }),
    ...(cursor ? { cursor } : {}),
  };
  return useQuery({
    queryKey: queryKeys.agents(companyId, "list", query),
    enabled,
    refetchInterval: LIVE_REFRESH_MS,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/agents", { params: { query: query as never } })).data!,
  });
}

// ── Ajan ayrıntısı ───────────────────────────────────────────────────────────
export function useAgent(id: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.agents(id ?? "none", "agent"),
    enabled: enabled && id !== null,
    refetchInterval: LIVE_REFRESH_MS,
    refetchOnWindowFocus: true,
    queryFn: async () => (await api.GET("/agents/{id}", { params: { path: { id: id! } } })).data!,
  });
}

export function useAgentHeartbeats(id: string | null, enabled: boolean) {
  const query = { limit: 60 };
  return useQuery({
    queryKey: queryKeys.agents(id ?? "none", "heartbeats", query),
    enabled: enabled && id !== null,
    refetchInterval: LIVE_REFRESH_MS,
    queryFn: async () =>
      (await api.GET("/agents/{id}/heartbeats", { params: { path: { id: id! }, query } })).data!,
  });
}

export function useAgentCommands(
  id: string | null,
  filters: { status?: AgentCommandStatus | undefined },
  cursor: string | undefined,
  enabled: boolean,
) {
  const query: Record<string, string | number> = {
    limit: 25,
    ...(filters.status ? { status: filters.status } : {}),
    ...(cursor ? { cursor } : {}),
  };
  return useQuery({
    queryKey: queryKeys.agents(id ?? "none", "commands", query),
    enabled: enabled && id !== null,
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/agents/{id}/commands", { params: { path: { id: id! }, query: query as never } }))
        .data!,
  });
}

export function useAgentAlarms(
  id: string | null,
  filters: { severity?: AgentAlarmSeverity | undefined },
  cursor: string | undefined,
  enabled: boolean,
) {
  const query: Record<string, string | number> = {
    limit: 25,
    ...(filters.severity ? { severity: filters.severity } : {}),
    ...(cursor ? { cursor } : {}),
  };
  return useQuery({
    queryKey: queryKeys.agents(id ?? "none", "alarms", query),
    enabled: enabled && id !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/agents/{id}/alarms", { params: { path: { id: id! }, query: query as never } })).data!,
  });
}

/**
 * Günlük yüklemeleri: `downloadUrl` KISA ÖMÜRLÜ ön imzalı adrestir (`downloadExpiresAt`). Bu yüzden
 * önbellekte bekletilmez — sekme her açıldığında taze adres alınır.
 */
export function useAgentLogUploads(id: string | null, cursor: string | undefined, enabled: boolean) {
  const query: Record<string, string | number> = { limit: 25, ...(cursor ? { cursor } : {}) };
  return useQuery({
    queryKey: queryKeys.agents(id ?? "none", "log-uploads", query),
    enabled: enabled && id !== null,
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/agents/{id}/log-uploads", { params: { path: { id: id! }, query: query as never } }))
        .data!,
  });
}

// ── Kayıt kodları ────────────────────────────────────────────────────────────
export function useEnrollmentCodes(companyId: string, stationId: string | null, enabled: boolean) {
  const query = { companyId, ...(stationId ? { stationId } : {}) };
  return useQuery({
    queryKey: queryKeys.agents(companyId, "enrollment-codes", query),
    enabled,
    queryFn: async () => (await api.GET("/agents/enrollment-codes", { params: { query } })).data!.items,
  });
}

/**
 * Bir ajan işlemi birden çok ekranı birden değiştirir (pano özeti, liste, ayrıntı, komut geçmişi):
 * modülün tüm önbelleği birlikte tazelenir.
 */
export function useRefreshAgents(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.agents() });
  };
}
